import React, { useEffect, useMemo, useState } from "react";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  styled,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TabProps,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import { InvestorAutocomplete } from "components/Autocompletes/InvestorAutocomplete";
import { FileViewer } from "components/popups";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useCanCreateDeal, usePrompt } from "hooks";
import ReactHtmlParser from "html-react-parser";
import { useDealActions, useInvestorsSelector, useTaskDealsSelect, useTaskManageActions } from "store";
import { DealBase, FundFileType, ICategorizedFile, IEmail } from "store/types";

import { FileType, fundFilesTypes } from "../../../../constants";
import { CreateDealContent } from "../../../Prompts/CreateDealContent";

const dividerStyles: TabProps["sx"] = {
  minWidth: "1px",
  minHeight: "20px",
  maxHeight: "20px"
};

const commonStyles: TabProps["sx"] = {
  minHeight: "20px",
  maxHeight: "25px",
  padding: "0px",
  fontSize: "15px",
  color: "#5d7782",
  minWidth: "50px"
};

interface IFileValues {
  file_id: number;
  type: FundFileType | null;
  investor_id: number | null;
}

interface IEmailPreviewContentProps {
  email: IEmail | null;
  onCancel: () => void;
  onSubmit: (data: { email: IEmail; deal: DealBase; categorizedFiles?: ICategorizedFile[] }) => void;
  isReAssign?: boolean;
}

export const EmailPreviewContent: React.FC<IEmailPreviewContentProps> = ({
  email,
  onCancel,
  onSubmit,
  isReAssign = false
}) => {
  const canCreateDeal = useCanCreateDeal();
  const openPrompt = usePrompt();
  const investors = useInvestorsSelector();
  const deals = useTaskDealsSelect();
  const { getDealsAction } = useTaskManageActions();
  const [fileViewer, setFileViewer] = useState<{ id: number; url: string; name: string; type: FileType } | null>(null);
  const [filesValues, setFilesValues] = useState<IFileValues[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [deal, setDeal] = useState<DealBase | null>(email?.suggestions?.[0]?.deal || null);
  const { getDealInvestors } = useDealActions();
  const [dealInvestors, setDealInvestors] = useState<{ id: number; name: string }[]>([]);

  const filesList = useMemo(() => {
    if (!email) return [];
    return isReAssign ? email.original_attachments : email.attachments;
  }, [email, isReAssign]);

  const { action: getDealInvestorsAction, loading: dealInvestorsLoading } = useActionHook(getDealInvestors, {
    onSuccess: (data) => {
      setDealInvestors(data.map((item) => ({ id: item.id, name: item.name })));
    }
  });

  useEffect(() => {
    if (deal?.id) {
      getDealInvestorsAction({ dealId: deal.id });
    }
  }, [deal]);

  if (!email) return null;

  const isEmailHasFilesOnly = !!email.attachments.length && !email.email_body.length;
  const isEmailHasBodyOnly = Boolean(!email.attachments.length) && !!email.email_body.length;
  const isEmailHasBoth = !!email.attachments.length && !!email.email_body.length;

  const formattedDate = Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    day: "numeric",
    month: "short"
  }).format(new Date(email.created_at));

  const emailSender = useMemo(() => {
    if (!email.sender_name.length) return;
    if (email.sender_name.trim().indexOf(" ") !== -1) {
      //there is at least one space, excluding leading and training spaces
      return `${email.sender_name.split(" ")[0][0]}${email.sender_name.split(" ")[1][0]}`;
    } else return `${email.sender_name.split("")[0][0]}`;
  }, [email.sender_name]);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const TableCellStyled = styled(TableCell)(() => ({
    padding: "6px 10px"
  }));

  const onCreateDeal = async () => {
    const data = await openPrompt({
      Component: CreateDealContent
    });
    if (data) {
      await getDealsAction();
      setDeal({ id: data?.dealId, title: data?.dealTitle } as DealBase);
    }
  };

  useEffect(() => {
    if (filesList?.length) {
      setFilesValues(
        filesList.map((file) => {
          return { file_id: file.id, type: null, investor_id: null };
        })
      );
    } else {
      setFilesValues([]);
    }
  }, [isReAssign, filesList]);

  const getInvestorValue = (fileId: number) => {
    const fileValue = filesValues.find((item) => item.file_id === fileId);
    if (!fileValue) return null;
    return investors.find((option) => fileValue.investor_id === option.id) || null;
  };

  const isAssociatedAvailable = useMemo(() => {
    return Boolean(!email.deal_id) || isReAssign;
  }, [email.deal_id, isReAssign]);

  const isFilesValuesValid = useMemo(() => {
    if (!filesValues.length) return true;
    return !filesValues.some((item) => item.investor_id && !item.type);
  }, [filesValues]);

  const associateTooltipMessage = useMemo(() => {
    if (!isFilesValuesValid) return "File type is required when the investor field is selected";
    if (!deal) return "Deal is required";
    return "";
  }, [isFilesValuesValid, deal]);

  const renderFileCategorizing = useMemo(() => {
    return email.deal_id && !isReAssign ? (
      <Stack direction="column" gap="15px" sx={{ maxHeight: "400px", overflowY: "auto", width: "100%" }}>
        {email.attachments.map((file, index) => (
          <Typography
            sx={{ cursor: "pointer", color: "#26a6ae" }}
            onClick={() =>
              file.file_url && setFileViewer({ id: file.id, url: file.file_url, name: file.name, type: file.type })
            }
            key={index}
          >
            {file.name}
          </Typography>
        ))}
      </Stack>
    ) : (
      <Stack sx={{ width: "100%" }}>
        <Stack
          direction="row"
          sx={{ justifyContent: "flex-end", alignItems: "center", gap: "15px", mb: "10px", padding: "0px 10px" }}
        >
          <Typography>Deal</Typography>
          <Autocomplete
            key={deal?.id}
            disableClearable
            sx={{ minWidth: 200 }}
            getOptionLabel={(option) => option.title}
            options={deals}
            value={deal || undefined}
            onChange={(_, option) => {
              option && setDeal(option);
            }}
            renderOption={(props, item) => (
              <li {...props} key={item.id}>
                {item.title}
              </li>
            )}
            renderInput={(params) => <TextField {...params} placeholder="Select" />}
            PaperComponent={({ children }) => {
              return (
                <Paper
                  sx={{
                    position: "absolute",
                    backgroundColor: "white",
                    zIndex: "10",
                    width: "100%",
                    borderRadius: "10px",
                    "& .MuiAutocomplete-option:hover": {
                      backgroundColor: "#edf1f9"
                    }
                  }}
                >
                  <>
                    {children}
                    {canCreateDeal && (
                      <Button
                        onMouseDown={onCreateDeal}
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          height: "50px",
                          backgroundColor: "#fff",
                          boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                          borderRadius: "0 0 10px 10px"
                        }}
                        fullWidth
                      >
                        Add Deal
                      </Button>
                    )}
                  </>
                </Paper>
              );
            }}
          />
        </Stack>
        <TableContainer sx={{ maxHeight: "400px" }}>
          <Table>
            <TableHead sx={{ bgcolor: "#F6F8FC" }}>
              <TableRow>
                <TableCellStyled>File Name</TableCellStyled>
                <TableCellStyled>File Type</TableCellStyled>
                <TableCellStyled>Investor</TableCellStyled>
              </TableRow>
            </TableHead>
            <TableBody>
              {filesList.map((file, index) => (
                <TableRow key={index}>
                  <TableCellStyled
                    style={{ cursor: "pointer", color: "#26a6ae" }}
                    onClick={() =>
                      file.file_url &&
                      setFileViewer({ id: file.id, url: file.file_url, name: file.name, type: file.type })
                    }
                  >
                    {file.name}
                  </TableCellStyled>
                  <TableCellStyled>
                    <Autocomplete
                      sx={{ minWidth: 200 }}
                      options={fundFilesTypes}
                      value={filesValues.find((item) => item.file_id === file.id)?.type}
                      onChange={(_, option) =>
                        setFilesValues((prevState) =>
                          prevState.map((item) =>
                            item.file_id === file.id
                              ? {
                                  ...item,
                                  type: option
                                }
                              : item
                          )
                        )
                      }
                      renderInput={(params) => <TextField {...params} placeholder="Select" />}
                      renderOption={(props, option) => {
                        return (
                          <li {...props} key={option}>
                            {option}
                          </li>
                        );
                      }}
                    />
                  </TableCellStyled>
                  <TableCellStyled>
                    <InvestorAutocomplete
                      sx={{ minWidth: 200 }}
                      loading={dealInvestorsLoading}
                      dealInvestors={dealInvestors}
                      preselectedInvestorId={getInvestorValue(file.id)?.id || null}
                      onChange={(option) => {
                        setFilesValues((prevState) =>
                          prevState.map((item) =>
                            item.file_id === file.id
                              ? {
                                  ...item,
                                  investor_id: option?.id || null
                                }
                              : item
                          )
                        );
                      }}
                    />
                  </TableCellStyled>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    );
  }, [investors, email, deals, filesValues, deal, dealInvestorsLoading, dealInvestors, isReAssign, filesList]);

  return (
    <Stack
      direction="column"
      justifyContent="space-between"
      sx={{
        ...style,
        backgroundColor: "#ffffff",
        borderRadius: "10px",
        width: "750px",
        minHeight: "400px",
        padding: "30px"
      }}
    >
      <Stack flexDirection="column" gap="20px">
        <Box>
          <Stack flexDirection="row" alignItems="center" justifyContent="space-between" padding="7px 0px">
            <Typography>Email Preview</Typography>
            <CloseIcon sx={{ cursor: "pointer" }} onClick={onCancel} />
          </Stack>
          <Divider sx={{ borderColor: "rgba(219, 232, 245, 1)" }} />
        </Box>
        <Box>
          <Stack direction="row" gap="6px" justifyContent="space-between" alignItems="center">
            <Stack direction="row" gap="6px" alignItems="center">
              <Avatar
                sx={{ backgroundColor: "#3a5b9f", width: 30, height: 30, fontSize: "10px" }}
                alt={email.sender_name}
                src={"/static/images/avatar/11.jpg"}
              >
                {emailSender}
              </Avatar>
              <Stack sx={{ maxWidth: "500px" }}>
                <Typography color="#0A3142" fontWeight={400}>
                  {email.sender_name}
                </Typography>
                <MaterialTooltip title={email.email_subject}>
                  <Typography
                    color="secondary"
                    fontSize="13px"
                    sx={{ maxWidth: "500px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {email.email_subject}
                  </Typography>
                </MaterialTooltip>
              </Stack>
            </Stack>
            <Stack alignItems="flex-end" maxHeight="30px">
              <Typography variant="body2" color="#5D7782">
                {formattedDate}
              </Typography>
              {Boolean(email?.attachments?.length) && (
                <Stack flexDirection="row" alignItems="center" justifyContent="center" gap="5px">
                  <AttachFileIcon sx={{ transform: "rotate(45deg)", fontSize: 20, color: "#5D7782" }} />
                  <Typography
                    variant="body2"
                    color="#5D7782"
                  >{`Attachments (${email?.attachments?.length})`}</Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Box>
        <Box>
          {isEmailHasBoth && (
            <Tabs value={tabValue} onChange={handleChange} sx={{ mt: 2, minHeight: "25px", mb: 2 }}>
              <Tab
                value={0}
                label={`Files (${email.attachments.length})`}
                iconPosition="start"
                icon={<InsertDriveFileOutlinedIcon sx={{ fontSize: "14px" }} />}
                sx={commonStyles}
              />
              <Tab
                sx={dividerStyles}
                label=""
                icon={<Divider sx={{ width: "1px", height: "16px", backgroundColor: "#dae0e2" }} />}
                disabled
              />
              <Tab
                value={1}
                label="Email Body"
                iconPosition="start"
                icon={<StickyNote2OutlinedIcon sx={{ fontSize: "14px" }} />}
                sx={commonStyles}
              />
            </Tabs>
          )}
          {!tabValue && isEmailHasBoth && (
            <Box display="flex" flexWrap="wrap" gap="15px" sx={{ padding: "10p", color: "#0a3142", maxWidth: "750px" }}>
              {renderFileCategorizing}
            </Box>
          )}
          {Boolean(tabValue) && isEmailHasBoth && (
            <Stack gap="10px">
              <Typography
                color="#0A3142"
                lineHeight="1"
                overflow="auto"
                maxHeight="50vh"
                sx={{
                  "::-webkit-scrollbar-thumb": {
                    backgroundColor: "#DAE0E2"
                  },
                  "& html": {
                    overflow: "auto !important",
                    height: "50vh !important",
                    maxHeight: "50vh !important",
                    minHeight: "50px !important"
                  }
                }}
              >
                {ReactHtmlParser(email.email_body)}
              </Typography>
            </Stack>
          )}
          {isEmailHasBodyOnly && (
            <>
              {(isReAssign || !email.deal_id) && (
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: "15px",
                    mb: "10px",
                    padding: "0px 10px"
                  }}
                >
                  <Typography>Deal</Typography>
                  <Autocomplete
                    key={deal?.id}
                    disableClearable
                    sx={{ minWidth: 200 }}
                    getOptionLabel={(option) => option.title}
                    options={deals}
                    value={deal || undefined}
                    onChange={(_, option) => {
                      option && setDeal(option);
                    }}
                    renderOption={(props, item) => (
                      <li {...props} key={item.id}>
                        {item.title}
                      </li>
                    )}
                    renderInput={(params) => <TextField {...params} placeholder="Select" />}
                    PaperComponent={({ children }) => {
                      return (
                        <Paper
                          sx={{
                            position: "absolute",
                            backgroundColor: "white",
                            zIndex: "10",
                            width: "100%",
                            borderRadius: "10px",
                            "& .MuiAutocomplete-option:hover": {
                              backgroundColor: "#edf1f9"
                            }
                          }}
                        >
                          <>
                            {children}
                            {canCreateDeal && (
                              <Button
                                onMouseDown={onCreateDeal}
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-start",
                                  height: "50px",
                                  backgroundColor: "#fff",
                                  boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                                  borderRadius: "0 0 10px 10px"
                                }}
                                fullWidth
                              >
                                Add Deal
                              </Button>
                            )}
                          </>
                        </Paper>
                      );
                    }}
                  />
                </Stack>
              )}

              <Stack gap="10px">
                <Typography
                  color="#0A3142"
                  lineHeight="1"
                  overflow="auto"
                  maxHeight="600px"
                  sx={{
                    "::-webkit-scrollbar-thumb": {
                      backgroundColor: "#DAE0E2"
                    },
                    "& html": {
                      overflow: "auto !important",
                      height: "600px !important",
                      maxHeight: "600px !important",
                      minHeight: "50px !important"
                    }
                  }}
                >
                  {ReactHtmlParser(email.email_body)}
                </Typography>
              </Stack>
            </>
          )}
          {isEmailHasFilesOnly && (
            <Stack gap="10px">
              <Box
                display="flex"
                flexWrap="wrap"
                gap="15px"
                sx={{ padding: "10p", color: "#0a3142", maxWidth: "500px" }}
              >
                {renderFileCategorizing}
              </Box>
            </Stack>
          )}
        </Box>
      </Stack>
      <Stack direction="row" sx={{ justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
        <Button variant="outlined" onClick={onCancel}>
          Close
        </Button>

        {isAssociatedAvailable && (
          <MaterialTooltip title={associateTooltipMessage} isShow>
            <span>
              <Button
                sx={{ padding: "0 12px", backgroundColor: "#071F35" }}
                variant="contained"
                color="primary"
                disabled={!deal || (Boolean(filesValues.length) && !isFilesValuesValid)}
                onClick={() =>
                  deal &&
                  onSubmit({
                    email,
                    deal,
                    categorizedFiles: filesValues.length ? (filesValues as ICategorizedFile[]) : undefined
                  })
                }
                startIcon={<ForwardToInboxIcon />}
              >
                Associate
              </Button>
            </span>
          </MaterialTooltip>
        )}
      </Stack>
      {fileViewer && (
        <FileViewer
          file={{ file_url: fileViewer.url, type: fileViewer.type }}
          isOpen={Boolean(fileViewer)}
          onClose={() => setFileViewer(null)}
          hideActions
        />
      )}
    </Stack>
  );
};

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  borderRadius: "10px"
};
